import { type FC, useState, useMemo, useContext, useEffect } from "react";
import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMembership } from "@/hooks/use-membership";
import { useOrganization } from "@/queries/organization-queries";
import { useEnterpriseCreditUsageReport } from "@/queries/enterprise-usage-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { Code, ConnectError } from "@connectrpc/connect";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/podkit/select/Select";
import { EnterpriseUsageDailyChart } from "@/routes/settings/organization/enterprise-usage/EnterpriseUsageDailyChart";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import {
    buildMonthRanges,
    buildDailyChartData,

    computeTopUsers,
    computeTotalCredits,
    extractUniqueUsers,
    formatCredits,
    formatUpdatedAt,
    type Grouping,
    type UsageTypeFilter,
} from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";

const EnterpriseUsagePage: FC = () => {
    useDocumentTitle("Usage");

    const { setBreadCrumbRowAction } = useContext(SettingsPageContext);

    // Computed once on mount — intentionally not reactive to date changes.
    // If the tab stays open past a month boundary the list won't update until remount.
    const monthRanges = useMemo(() => buildMonthRanges(), []);
    const [selectedMonth, setSelectedMonth] = useState(monthRanges[0]?.value ?? "");
    const [selectedType, setSelectedType] = useState<UsageTypeFilter>("all");
    const [grouping, setGrouping] = useState<Grouping>("users");

    const { data: organization, isLoading: isLoadingOrganization } = useOrganization();
    const { membership, isPending: isLoadingMembership } = useMembership();

    const organizationId = organization?.id ?? "";
    const activeRange = useMemo(
        () => monthRanges.find((r) => r.value === selectedMonth) ?? monthRanges[0],
        [monthRanges, selectedMonth],
    );

    const {
        data: reportData,
        isLoading: isLoadingReport,
        error,
    } = useEnterpriseCreditUsageReport(organizationId, activeRange.start, activeRange.end);

    const updatedAt = formatUpdatedAt(reportData?.updatedAt);

    // Always show the full calendar month on the chart, even when the API
    // range ends mid-month (current month). This avoids a nearly-empty chart
    // early in the month.
    const chartMonthEnd = useMemo(() => {
        const s = activeRange.start;
        return new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + 1, 0));
    }, [activeRange.start]);

    useEffect(() => {
        setBreadCrumbRowAction(
            <HeaderControls
                monthRanges={monthRanges}
                selectedMonth={selectedMonth}
                onMonthChange={setSelectedMonth}
                updatedAt={updatedAt}
            />,
        );

        return () => {
            setBreadCrumbRowAction(null);
        };
    }, [setBreadCrumbRowAction, monthRanges, selectedMonth, updatedAt]);

    if (isLoadingMembership || isLoadingOrganization) {
        return <LoadingState />;
    }

    if (membership?.userRole !== OrganizationRole.ADMIN) {
        return <NotAuthorized />;
    }

    if (error instanceof ConnectError && error.code === Code.PermissionDenied) {
        return <NotAuthorized />;
    }

    const errorMessage = deriveErrorMessage(error);
    const dailyUsage = reportData?.dailyUsage ?? [];

    return (
        <EnterpriseUsageContent
            dailyUsage={dailyUsage}
            isLoading={isLoadingReport}
            selectedMonthLabel={activeRange.label}
            monthStart={activeRange.start}
            monthEnd={chartMonthEnd}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            grouping={grouping}
            onGroupingChange={setGrouping}
            errorMessage={errorMessage}
        />
    );
};

export default EnterpriseUsagePage;

// ─── Sub-components ─────────────────────────────────────────────────────────

import type { PlainDailyCreditUsage } from "@/queries/enterprise-usage-queries";
import type { MonthRange } from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";

function deriveErrorMessage(error: Error | null): string | undefined {
    if (!error) return undefined;
    if (error instanceof ConnectError && (error.code === Code.NotFound || error.code === Code.FailedPrecondition)) {
        return "Usage data is not available for this organization.";
    }
    return "We\u2019re having trouble loading usage data. Try selecting a different month or refreshing the page.";
}

type HeaderControlsProps = {
    monthRanges: MonthRange[];
    selectedMonth: string;
    onMonthChange: (v: string) => void;
    updatedAt: string;
};

const HeaderControls: FC<HeaderControlsProps> = ({ monthRanges, selectedMonth, onMonthChange, updatedAt }) => (
    <div className="flex items-center gap-3">
        {updatedAt && (
            <Text className="hidden text-xs text-content-tertiary lg:block">
                Updated daily · Last update: {updatedAt}
            </Text>
        )}

        <Select value={selectedMonth} onValueChange={onMonthChange}>
            <SelectTrigger
                loading={false}
                className="inline-flex w-auto min-w-0 gap-1 rounded-md border-border-light px-3 py-1.5 text-sm"
            >
                <span>{monthRanges.find((r) => r.value === selectedMonth)?.label ?? selectedMonth}</span>
            </SelectTrigger>
            <SelectContent>
                {monthRanges.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                        <span>{r.label}</span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

type ContentProps = {
    dailyUsage: PlainDailyCreditUsage[];
    isLoading: boolean;
    selectedMonthLabel: string;
    monthStart: Date;
    monthEnd: Date;
    selectedType: UsageTypeFilter;
    onTypeChange: (t: UsageTypeFilter) => void;
    grouping: Grouping;
    onGroupingChange: (g: Grouping) => void;
    errorMessage?: string;
};

const EnterpriseUsageContent: FC<ContentProps> = ({
    dailyUsage,
    isLoading,
    selectedMonthLabel,
    monthStart,
    monthEnd,
    selectedType,
    onTypeChange,
    grouping,
    onGroupingChange,
    errorMessage,
}) => {
    const [selectedMember, setSelectedMember] = useState<string | undefined>();
    const topUsers = useMemo(() => computeTopUsers(dailyUsage, 5), [dailyUsage]);
    const allUsers = useMemo(() => extractUniqueUsers(dailyUsage), [dailyUsage]);
    const chartData = useMemo(
        () =>
            buildDailyChartData(dailyUsage, grouping, topUsers, {
                usageTypeFilter: selectedType,
                userFilter: selectedMember,
                monthStart,
                monthEnd,
            }),
        [dailyUsage, grouping, topUsers, selectedType, selectedMember, monthStart, monthEnd],
    );
    const totalCredits = useMemo(() => computeTotalCredits(dailyUsage, "all"), [dailyUsage]);
    const environmentCredits = useMemo(() => computeTotalCredits(dailyUsage, "environment"), [dailyUsage]);
    const agenticCredits = useMemo(() => computeTotalCredits(dailyUsage, "agentic"), [dailyUsage]);

    return (
        <div className="flex flex-col gap-6" data-testid="enterprise-usage-page">
            <Text className="text-base text-content-secondary">
                Track how your organization uses credits over time.
            </Text>
            <div className="grid max-w-[780px] grid-cols-2 gap-3 sm:grid-cols-3">
                <SummaryCard
                    label="Monthly total"
                    credits={totalCredits}
                    className="col-span-2 sm:col-span-1"
                    isActive={selectedType === "all"}
                    onClick={() => onTypeChange("all")}
                    data-tracking-id="usage-filter-all"
                />
                <SummaryCard
                    label="Environment"
                    credits={environmentCredits}
                    isActive={selectedType === "environment"}
                    onClick={() => onTypeChange("environment")}
                    data-tracking-id="usage-filter-environment"
                />
                <SummaryCard
                    label="Intelligence"
                    credits={agenticCredits}
                    isActive={selectedType === "agentic"}
                    onClick={() => onTypeChange("agentic")}
                    data-tracking-id="usage-filter-intelligence"
                />
            </div>

            <EnterpriseUsageDailyChart
                chartData={chartData}
                grouping={grouping}
                onGroupingChange={onGroupingChange}
                selectedType={selectedType}
                allUsers={allUsers}
                selectedMember={selectedMember}
                onMemberChange={setSelectedMember}
                loading={isLoading}
                monthLabel={selectedMonthLabel}
                errorMessage={errorMessage}
            />
        </div>
    );
};

const SummaryCard: FC<{
    label: string;
    credits: number;
    className?: string;
    isActive?: boolean;
    onClick?: () => void;
    "data-tracking-id"?: string;
}> = ({ label, credits, className, isActive, onClick, "data-tracking-id": trackingId }) => (
    <div
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        className={cn(
            "rounded-xl border border-border-light p-5 transition-colors duration-100",
            onClick && "cursor-pointer hover:bg-surface-hover",
            isActive
                ? "shadow-[0_0_0_1.5px] shadow-surface-button-primary-accent"
                : onClick
                  ? "shadow-[0_0_0_0.5px] shadow-border-base"
                  : "",
            className,
        )}
        onClick={onClick}
        onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
        data-tracking-id={trackingId}
    >
        <Text className="text-sm text-content-secondary">{label}</Text>
        <div className="mt-1 flex items-baseline gap-1.5">
            {credits > 0 ? (
                <>
                    <span className="text-2xl font-bold text-content-primary">{formatCredits(credits)}</span>
                    <span className="text-sm text-content-tertiary">credits</span>
                </>
            ) : (
                <span className="text-2xl text-content-tertiary">&mdash;</span>
            )}
        </div>
    </div>
);
